import { useWhatsNew } from '@src/stores-v2/StoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import MegaphoneWithSparklesIcon from '@src/svgs/MegaphoneWithSparklesIcon';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import ChangelogHistory from './ChangelogHistory';
import ReleaseCard from './ReleaseCard';

type DrawerView = 'latest' | 'history';

const WhatsNewDrawer: React.FC = observer(() => {
  const whatsNew = useWhatsNew();
  const [view, setView] = useState<DrawerView>('latest');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        whatsNew.closeDrawer();
      }
    };

    if (whatsNew.isDrawerOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [whatsNew.isDrawerOpen]);

  // Set view based on whether user has already seen the latest release
  useEffect(() => {
    if (whatsNew.isDrawerOpen) {
      setView(whatsNew.isShowingNewRelease ? 'latest' : 'history');
    }
  }, [whatsNew.isDrawerOpen]);

  const handleBackdropClick = useCallback(() => {
    whatsNew.closeDrawer();
  }, [whatsNew]);

  const handlePanelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return createPortal(
    <>
      <Backdrop
        $isOpen={whatsNew.isDrawerOpen}
        onClick={handleBackdropClick}
        aria-hidden
      />
      <Panel
        $isOpen={whatsNew.isDrawerOpen}
        onClick={handlePanelClick}
        role="dialog"
        aria-modal="true"
        aria-label="What's new"
        aria-hidden={!whatsNew.isDrawerOpen}
        {...(!whatsNew.isDrawerOpen && { inert: '' })}
      >
        <DrawerHeader>
          <HeaderLeft>
            {view === 'history' && (
              <BackButton onClick={() => setView('latest')} aria-label="Back">
                &larr;
              </BackButton>
            )}
            {view === 'latest' && <MegaphoneWithSparklesIcon />}
            <HeaderTitle>
              {view === 'latest' ? "What's New in Arbor" : 'All Releases'}
            </HeaderTitle>
          </HeaderLeft>
          <CloseButton
            onClick={() => whatsNew.closeDrawer()}
            aria-label="Close what's new"
          >
            <CloseIcon />
          </CloseButton>
        </DrawerHeader>

        <DrawerBody>
          {whatsNew.isLoading && !whatsNew.latestRelease && (
            <StatusText>Loading release notes...</StatusText>
          )}

          {whatsNew.error && !whatsNew.latestRelease && (
            <StatusText $error>{whatsNew.error}</StatusText>
          )}

          {view === 'latest' && whatsNew.latestRelease && (
            <>
              <ReleaseCard release={whatsNew.latestRelease} />
              <ViewAllButton onClick={() => setView('history')}>
                View all releases
              </ViewAllButton>
            </>
          )}

          {view === 'latest' &&
            !whatsNew.latestRelease &&
            !whatsNew.isLoading && (
              <StatusText>No release notes available yet.</StatusText>
            )}

          {view === 'history' && <ChangelogHistory />}
        </DrawerBody>
      </Panel>
    </>,
    document.body,
  );
});

export default WhatsNewDrawer;

const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  /* Same blur as the modal backdrop. */
  backdrop-filter: var(--modal-blur);
  -webkit-backdrop-filter: var(--modal-blur);
  z-index: 1000009;
  opacity: ${p => (p.$isOpen ? 1 : 0)};
  pointer-events: ${p => (p.$isOpen ? 'auto' : 'none')};
  transition: opacity var(--transition-normal) cubic-bezier(0.4, 0, 0.2, 1);
`;

const Panel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 504px;
  max-width: 90vw;
  height: 100vh;
  background: var(--background);
  border-left: 1px solid var(--border-subtle);
  z-index: 1000010;
  display: flex;
  flex-direction: column;
  box-shadow: var(--elevation-lg);
  transform: translateX(${p => (p.$isOpen ? '0' : '100%')});
  transition: transform var(--transition-sidebar) cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-input);
  font-size: 16px;
  transition: all var(--transition-fast) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-input);
  padding: 0;
  transition: all var(--transition-fast) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 var(--spacing-lg) var(--spacing-lg);
`;

const ViewAllButton = styled.button`
  display: block;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-md);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
    color: var(--foreground);
  }
`;

const StatusText = styled.div<{ $error?: boolean }>`
  text-align: center;
  padding: var(--spacing-2xl) var(--spacing-lg);
  color: ${p => (p.$error ? 'var(--error, #ef4444)' : 'var(--text-muted)')};
  font-size: var(--text-sm);
`;
